import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

// Supabase custom field type
export type CustomField = {
  id: string; // UUID
  created_at: string; // ISO timestamp
  name: string;
  type: 'text' | 'date' | 'dropdown' | 'boolean' | 'number';
  scope: 'organization' | 'project' | 'folder' | 'list'; // Note: 'space' -> 'project' in Supabase
  scope_id?: string | null;
  options?: Array<{
    value: string;
    label: string;
    color?: string;
    icon?: string;
  }> | null;
  color?: string | null;
  icon?: string | null;
  order_index: number;
  created_at_ms: number; // timestamp in milliseconds (derived)
};

// Supabase row type for custom_fields table
interface SupabaseCustomFieldRow {
  id: string;
  created_at: number; // BIGINT in DB (milliseconds)
  name: string;
  type: 'text' | 'date' | 'dropdown' | 'boolean' | 'number';
  scope: 'organization' | 'project' | 'folder' | 'list';
  scope_id: string | null;
  options: Array<{ value: string; label: string; color?: string; icon?: string }> | null;
  color: string | null;
  icon: string | null;
  order_index: number;
  created_at_ms?: number;
}

// Helper to convert Supabase custom field to CustomField format
function convertCustomFieldFromSupabase(row: SupabaseCustomFieldRow): CustomField & { id: string } {
  const createdAtMs = row.created_at ? Number(row.created_at) : row.created_at_ms || Date.now();

  return {
    ...row,
    id: row.id,
    created_at: new Date(createdAtMs).toISOString(),
    created_at_ms: createdAtMs,
  } as CustomField & { id: string };
}

export async function fetchCustomFields(
  scope?: 'organization' | 'project' | 'folder' | 'list',
  scopeId?: string
): Promise<Array<CustomField & { id: string }>> {
  const { data: fields, error } = await withRetry(() => {
    let query = supabase.from('custom_fields').select('*');

    if (scope === 'organization') {
      query = query.eq('scope', 'organization').is('scope_id', null);
    } else if (scope && scopeId) {
      query = query.eq('scope', scope).eq('scope_id', scopeId);
    } else if (scope) {
      query = query.eq('scope', scope);
    }

    return query.order('order_index', { ascending: true });
  });

  if (error) throw error;
  return (fields || []).map(convertCustomFieldFromSupabase);
}

export async function fetchAllCustomFields(): Promise<Array<CustomField & { id: string }>> {
  const { data: fields, error } = await withRetry(() =>
    supabase
      .from('custom_fields')
      .select('*')
      .order('order_index', { ascending: true })
  );

  if (error) throw error;
  return (fields || []).map(convertCustomFieldFromSupabase);
}

// Helper to check if a document is a container
function isContainer(doc: unknown): doc is { id: string; type: 'project' | 'folder' | 'list'; parent_id: string | null } {
  return Boolean(doc && typeof doc === 'object' && 'type' in doc && ((doc as { type: string }).type === 'project' || (doc as { type: string }).type === 'folder' || (doc as { type: string }).type === 'list'));
}

export async function fetchCustomFieldsForContext(
  spaceId?: string,
  folderId?: string,
  listId?: string
): Promise<Array<CustomField & { id: string }>> {
  const fieldMap = new Map<string, CustomField>();

  // Organization-wide fields
  const { data: orgFields } = await withRetry(() =>
    supabase
      .from('custom_fields')
      .select('*')
      .eq('scope', 'organization')
      .is('scope_id', null)
  );

  (orgFields || []).forEach(field => {
    const converted = convertCustomFieldFromSupabase(field);
    fieldMap.set(converted.id, converted);
  });

  // Determine container type and IDs from containerId
  let projectContainerId: string | undefined;
  let folderContainerId: string | undefined;
  let listContainerId: string | undefined;

  if (listId) {
    const { data: containerDoc } = await withRetry(() =>
      supabase
        .from('spaces')
        .select('id, type, parent_id')
        .eq('id', listId)
        .single()
    );

    if (isContainer(containerDoc) && containerDoc.type === 'list') {
      listContainerId = listId;
      // Get parent hierarchy
      if (containerDoc.parent_id) {
        const { data: parentDoc } = await withRetry(() =>
          supabase
            .from('spaces')
            .select('id, type, parent_id')
            .eq('id', containerDoc.parent_id)
            .single()
        );

        if (isContainer(parentDoc)) {
          if (parentDoc.type === 'folder') {
            folderContainerId = parentDoc.id;
            // Get root project
            if (parentDoc.parent_id) {
              const { data: rootProjectDoc } = await withRetry(() =>
                supabase
                  .from('spaces')
                  .select('id, type')
                  .eq('id', parentDoc.parent_id)
                  .single()
              );

              if (isContainer(rootProjectDoc) && rootProjectDoc.type === 'project') {
                projectContainerId = rootProjectDoc.id;
              }
            }
          } else if (parentDoc.type === 'project') {
            projectContainerId = parentDoc.id;
          }
        }
      }
    }
  } else if (folderId) {
    const { data: containerDoc } = await withRetry(() =>
      supabase
        .from('spaces')
        .select('id, type, parent_id')
        .eq('id', folderId)
        .single()
    );

    if (isContainer(containerDoc) && containerDoc.type === 'folder') {
      folderContainerId = folderId;
      // Get root project
      if (containerDoc.parent_id) {
        const { data: rootProjectDoc } = await withRetry(() =>
          supabase
            .from('spaces')
            .select('id, type')
            .eq('id', containerDoc.parent_id)
            .single()
        );

        if (isContainer(rootProjectDoc) && rootProjectDoc.type === 'project') {
          projectContainerId = rootProjectDoc.id;
        }
      }
    }
  } else if (spaceId) {
    const { data: containerDoc } = await withRetry(() =>
      supabase
        .from('spaces')
        .select('id, type, parent_id')
        .eq('id', spaceId)
        .single()
    );

    if (isContainer(containerDoc)) {
      if (containerDoc.type === 'list') {
        listContainerId = spaceId;
        // Get parent hierarchy
        if (containerDoc.parent_id) {
          const { data: parentDoc } = await withRetry(() =>
            supabase
              .from('spaces')
              .select('id, type, parent_id')
              .eq('id', containerDoc.parent_id)
              .single()
        );

          if (isContainer(parentDoc)) {
            if (parentDoc.type === 'folder') {
              folderContainerId = parentDoc.id;
              if (parentDoc.parent_id) {
                const { data: rootProjectDoc } = await withRetry(() =>
                  supabase
                    .from('spaces')
                    .select('id, type')
                    .eq('id', parentDoc.parent_id)
                    .single()
                );

                if (isContainer(rootProjectDoc) && rootProjectDoc.type === 'project') {
                  projectContainerId = rootProjectDoc.id;
                }
              }
            } else if (parentDoc.type === 'project') {
              projectContainerId = parentDoc.id;
            }
          }
        }
      } else if (containerDoc.type === 'folder') {
        folderContainerId = spaceId;
        if (containerDoc.parent_id) {
          const { data: rootProjectDoc } = await withRetry(() =>
            supabase
              .from('spaces')
              .select('id, type')
              .eq('id', containerDoc.parent_id)
              .single()
          );

          if (isContainer(rootProjectDoc) && rootProjectDoc.type === 'project') {
            projectContainerId = rootProjectDoc.id;
          }
        }
      } else if (containerDoc.type === 'project') {
        projectContainerId = spaceId;
      }
    }
  }

  // Project-specific fields
  if (projectContainerId) {
    const { data: projectFields } = await withRetry(() =>
      supabase
        .from('custom_fields')
        .select('*')
        .eq('scope', 'project')
        .eq('scope_id', projectContainerId)
    );

    (projectFields || []).forEach(field => {
      const converted = convertCustomFieldFromSupabase(field);
      fieldMap.set(converted.id, converted);
    });
  }

  // Folder-specific fields
  if (folderContainerId) {
    const { data: folderFields } = await withRetry(() =>
      supabase
        .from('custom_fields')
        .select('*')
        .eq('scope', 'folder')
        .eq('scope_id', folderContainerId)
    );

    (folderFields || []).forEach(field => {
      const converted = convertCustomFieldFromSupabase(field);
      fieldMap.set(converted.id, converted);
    });
  }

  // List-specific fields
  if (listContainerId) {
    const { data: listFields } = await withRetry(() =>
      supabase
        .from('custom_fields')
        .select('*')
        .eq('scope', 'list')
        .eq('scope_id', listContainerId)
    );

    (listFields || []).forEach(field => {
      const converted = convertCustomFieldFromSupabase(field);
      fieldMap.set(converted.id, converted);
    });
  }

  const fields = Array.from(fieldMap.values());
  return fields.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
}

export async function createCustomField(field: Omit<CustomField, 'id'>): Promise<CustomField & { id: string }> {
  const createdAt = field.created_at_ms || Date.now();

  const { data: newField, error } = await withRetry(() =>
    supabase
      .from('custom_fields')
      .insert({
      name: field.name,
      type: field.type,
      scope: field.scope, // scope is already 'project' | 'folder' | 'list' | 'organization'
      scope_id: field.scope_id || null,
      options: field.options || null,
      color: field.color || null,
      icon: field.icon || null,
      order_index: field.order_index ?? 0,
      created_at: createdAt,
    })
    .select()
    .single()
  );

  if (error || !newField) {
    throw new Error(error?.message || 'Failed to create custom field');
  }
  return convertCustomFieldFromSupabase(newField);
}

export async function updateCustomField(
  id: string,
  updates: Partial<Omit<CustomField, 'id'>>
): Promise<(CustomField & { id: string }) | null> {
  const updateData: Record<string, unknown> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.type !== undefined) updateData.type = updates.type;
  if (updates.scope !== undefined) updateData.scope = updates.scope;
  if (updates.scope_id !== undefined) updateData.scope_id = updates.scope_id || null;
  if (updates.options !== undefined) updateData.options = updates.options || null;
  if (updates.color !== undefined) updateData.color = updates.color || null;
  if (updates.icon !== undefined) updateData.icon = updates.icon || null;
  if (updates.order_index !== undefined) updateData.order_index = updates.order_index;

  const { data: updatedField, error } = await withRetry(() =>
    supabase
      .from('custom_fields')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !updatedField) return null;
  return convertCustomFieldFromSupabase(updatedField);
}

export async function deleteCustomField(id: string): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('custom_fields')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
  return true;
}

export async function reorderCustomFields(ids: string[]): Promise<Array<CustomField & { id: string }>> {
  // Update order_index for each field
  const updates = ids.map((id, index) =>
    withRetry(() =>
      supabase
        .from('custom_fields')
        .update({ order_index: index })
        .eq('id', id)
    )
  );
  await Promise.all(updates);

  // Fetch updated fields
  const { data: fields, error } = await withRetry(() =>
    supabase
      .from('custom_fields')
      .select('*')
      .in('id', ids)
      .order('order_index', { ascending: true })
  );

  if (error) throw error;
  return (fields || []).map(convertCustomFieldFromSupabase);
}
